import { ReactNode, useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { ensureSupabaseAuthSession, useAuth } from "@/hooks/useAuth";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import { supabase } from "@/integrations/supabase/client";

interface ProtectedRouteProps {
  children: ReactNode;
  /** "auth"     -> requer apenas usuário logado
   *  "member"   -> requer usuário + membership ativa em alguma empresa
   *  "admin"    -> requer super admin
   */
  requires?: "auth" | "member" | "admin";
}

const Spinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
  </div>
);

export default function ProtectedRoute({ children, requires = "member" }: ProtectedRouteProps) {
  const { user, session, isLoading: authLoading } = useAuth();
  const { isSuperAdmin, isLoading: adminLoading } = useSuperAdmin();
  const location = useLocation();

  const [membershipState, setMembershipState] = useState<"unknown" | "checking" | "has" | "none">(
    requires === "member" ? "unknown" : "has"
  );
  const lastCheckedUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (user) {
      try {
        sessionStorage.removeItem("fastblog_auth_redirect_reason");
      } catch {
        /* ignore */
      }
    }
  }, [user?.id]);

  useEffect(() => {
    if (requires !== "member") return;
    if (authLoading) return;
    if (!user) {
      setMembershipState(session ? "checking" : "none");
      lastCheckedUserRef.current = null;
      return;
    }
    // Evita re-checagem em re-renders quando o user é o mesmo.
    if (lastCheckedUserRef.current === user.id && membershipState === "has") return;
    let cancelled = false;
    setMembershipState("checking");
    (async () => {
      await ensureSupabaseAuthSession(session);
      const { data, error } = await supabase
        .from("company_members")
        .select("company_id")
        .eq("user_id", user.id)
        .eq("status", "active")
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        console.warn("[ProtectedRoute] membership lookup failed:", error.message);
        // Falha transitória: mantém acesso para não transformar instabilidade em logout.
        setMembershipState("has");
        lastCheckedUserRef.current = user.id;
        return;
      }
      setMembershipState(data ? "has" : "none");
      lastCheckedUserRef.current = user.id;
    })();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id, requires]);

  // STRICT LOADING GUARD: nunca redireciona enquanto a sessão está carregando.
  if (authLoading) return <Spinner />;

  // Mesmo com authLoading=false, se temos session mas o user ainda não foi
  // hidratado (janela curtíssima), seguramos o spinner em vez de mandar para /auth.
  if (!user && session) return <Spinner />;

  if (!user) {
    try {
      sessionStorage.setItem("fastblog_auth_redirect_reason", "protected_route:no_valid_user");
    } catch {
      /* ignore */
    }
    return <Navigate to="/auth" replace state={{ from: location.pathname }} />;
  }

  if (requires === "admin") {
    if (adminLoading) return <Spinner />;
    if (!isSuperAdmin) return <Navigate to="/dashboard" replace />;
    return <>{children}</>;
  }

  if (requires === "member") {
    if (lastCheckedUserRef.current !== user.id) return <Spinner />;
    if (membershipState === "unknown" || membershipState === "checking") return <Spinner />;
    if (membershipState === "none") {
      // Super admin sem empresa pode acessar o dashboard mesmo assim.
      if (!adminLoading && isSuperAdmin) return <>{children}</>;
      try {
        sessionStorage.setItem("fastblog_auth_redirect_reason", "protected_route:no_active_membership");
      } catch {
        /* ignore */
      }
      return <Navigate to="/onboarding" replace />;
    }
  }

  return <>{children}</>;
}